import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import Markdown from "markdown-to-jsx";
import { useChat } from "src/Hooks/ChatContext";
import { s3baseUrl } from "src/config/config";
import LinkPopup from "./LinkPopup";
import GeneralPopUpModel from "src/components/GeneralComponents/GeneralPopUpModel";
import CustomConfirmation from "src/components/GeneralComponents/CustomConfirmation";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import CustomPopover from "src/components/CustomPopover";
import { get_local_preview, getTimeBasedOnDate } from "src/utils/constants";
import { update_image_on_s3_for_chat } from "src/DAL/Chat/Chat";
import { gr } from "src/design-system/tokens";
import GrLoader from "src/design-system/components/GrLoader";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrAvatar from "src/design-system/components/GrAvatar";
import GrChatBubble, {
  GrChatDateDivider,
} from "src/design-system/components/GrChatBubble";
import GrChatComposer from "src/design-system/components/GrChatComposer";

const noChatSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
};

const bottomSx = {
  flex: "none",
  width: "100%",
  p: gr.space[3],
  background: gr.color.surface,
  borderTop: `1px solid ${gr.color.line}`,
  fontFamily: gr.font.family,
};

const sendSx = { color: gr.color.brand };

const previewSx = {
  position: "relative",
  alignSelf: "flex-start",
  m: gr.space[2],
  "& img": {
    display: "block",
    height: 80,
    width: 100,
    objectFit: "cover",
    borderRadius: gr.radius.chip,
  },
};

const previewRemoveSx = {
  position: "absolute",
  top: `calc(-1 * ${gr.space[2]})`,
  right: `calc(-1 * ${gr.space[2]})`,
  background: gr.color.surface,
  border: `1px solid ${gr.color.line}`,
  borderRadius: gr.radius.pill,
};

const restrictedSx = {
  m: 0,
  flex: 1,
  p: gr.space[3],
  borderRadius: gr.radius.control,
  background: gr.color.bg,
  fontSize: gr.font.size.sm,
  color: gr.color.ink2,
};

const looksLikeHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));

const ChatContent = ({ content, renderLink }) => {
  if (!content) return null;
  if (looksLikeHtml(content)) {
    return (
      <div
        className="chat-html-content"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }
  return (
    <Markdown
      options={{
        overrides: { a: { component: renderLink } },
      }}
    >
      {content}
    </Markdown>
  );
};

function ChatMessages({ is_popup }) {
  const { enqueueSnackbar } = useSnackbar();
  const {
    selectedChat,
    selectedUser,
    loadMoreData,
    messagesList,
    lastBookElementRef,
    setSelectedMessage,
    selectedMessage,
    isLoadingMore,
    isLoadingChat,
    checkLoadMore,
    friendList,
  } = useChat();

  const inputRef = useRef(null);
  // Day label per message, computed once per list change (not on every keystroke in the composer).
  const messageDays = useMemo(
    () =>
      messagesList.map((m) => getTimeBasedOnDate(m?.createdAt, "DD MMM YYYY")),
    [messagesList]
  );
  // Divider label for today's messages ("Today" instead of the date); same format as messageDays.
  const todayDay = getTimeBasedOnDate(new Date().toISOString(), "DD MMM YYYY");
  const { userInfo, socket } = useContentSetting();
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [message, setMessage] = useState("");
  const [image, setImage] = useState("");
  const [file, setFile] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [openFormatter, setOpenFormatter] = useState(false);
  const [openLink, setOpenLink] = useState(false);
  const [inputHeight, setInputHeight] = useState(0);

  const receiver_profile = selectedChat?.receiver_profile;

  const handleUploadFile = (e) => {
    const _file = e.target.files[0];
    setFile(_file);
  };

  const handleRemoveFile = (e) => {
    setFile(null);
    setImage(null);
  };

  const handleCancelUpdate = async () => {
    setSelectedMessage(null);
    setMessage("");
    setFile(null);
    setImage(null);
    setIsUpdate(false);
  };

  const handleEdit = async (message_obj) => {
    setSelectedMessage(message_obj);
    setMessage(message_obj?.content);
    setImage(message_obj?.image);
    setIsUpdate(true);
    // Put the cursor in the composer so it is clear the message is being edited.
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSendMessage = async (receiver_id, action) => {
    let image = "";
    let is_error = false;
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("width", 600);
      formData.append("height", 700);
      const result = await update_image_on_s3_for_chat(formData);
      if (result.code === 200) {
        image = result.image_path;
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        is_error(true);
      }
    }
    if (is_error) {
      setIsLoading(false);
      return;
    }
    if (!message && !image) {
      setIsLoading(false);
      enqueueSnackbar("Please enter a message", { variant: "error" });
      return;
    }
    const postData = {
      chat_with: receiver_id,
      message_content: message,
      image: image,
      created_by: action,
      creator_id: userInfo._id,
    };
    socket.emit("send_chat_message", postData);
    setMessage("");
    setFile(null);
    setImage(null);
    setIsLoading(false);
  };

  const handleSubmitMessage = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (isUpdate) {
      handleUpdateMessage();
      return;
    }
    if (selectedUser) {
      handleSendMessage(selectedUser.chatId, "member_user");
    } else {
      handleSendMessage(selectedChat?.receiver_profile?.id, "member_user");
    }
  };

  const handleDelete = async (message_obj) => {
    setSelectedMessage(message_obj);
    setOpenDelete(true);
  };

  const handleAgreeDelete = async () => {
    setOpenDelete(false);
    const postData = {
      message_id: selectedMessage.messageId,
      creator_id: userInfo._id,
      chat_with: selectedChat?.receiver_profile?.id,
    };
    socket.emit("delete_chat_message", postData);
  };

  const handleUpdateMessage = async () => {
    let _image = image;
    let is_error = false;
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("width", 600);
      formData.append("height", 700);
      const result = await update_image_on_s3_for_chat(formData);
      if (result.code === 200) {
        _image = result.image_path;
        setIsLoading(false);
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        is_error(true);
      }
    }
    if (is_error) {
      setIsLoading(false);
      return;
    }
    if (!message && !_image) {
      setIsLoading(false);
      enqueueSnackbar("Please enter a message", { variant: "error" });
      return;
    }

    const postData = {
      message_id: selectedMessage.messageId,
      new_message_content: message,
      creator_id: userInfo._id,
      chat_with: selectedChat?.receiver_profile?.id,
      image: _image,
    };

    setMessage("");
    setFile(null);
    setImage(null);
    setSelectedMessage(null);
    setIsUpdate(false);
    setIsLoading(false);
    socket.emit("update_chat_message", postData);
  };

  // The composer's own textarea (via ref): the id "inputField" can exist twice on phones (page + floating box).
  const handleGetSelectedText = () => {
    const inputField = inputRef.current;
    if (inputField) {
      const selectedText = inputField.value.substring(
        inputField.selectionStart,
        inputField.selectionEnd
      );
      return selectedText;
    }
  };

  const handleOnBold = () => {
    let seleted_text = handleGetSelectedText();
    if (seleted_text) {
      const inputField = inputRef.current;
      var newText = inputField.value.replace(
        seleted_text,
        "**" + seleted_text + "**"
      );
      setMessage(newText);
    }
  };

  const handleOnItalic = () => {
    let seleted_text = handleGetSelectedText();
    if (seleted_text) {
      const inputField = inputRef.current;
      var newText = inputField.value.replace(
        seleted_text,
        "*" + seleted_text + "*"
      );
      setMessage(newText);
    }
  };

  const handleAddLink = (values) => {
    let make_link = ` [${values.display_text}](${values.link_address})`;
    const inputField = inputRef.current;
    const start = inputField.selectionStart;
    const end = inputField.selectionEnd;
    setMessage(message.slice(0, start) + make_link + message.slice(end));
    inputField.setSelectionRange(
      start + make_link.length,
      start + make_link.length
    );
    setOpenLink(false);
  };

  const handleOpenLinkPopup = () => {
    setOpenLink(true);
  };

  const handleKeyDown = (e) => {
    // Enter sends; Shift+Enter makes a new line (before, Shift+Enter also sent — Mohsin: fix issues).
    if (e.key === "Enter" && !e.shiftKey) {
      handleSubmitMessage(e);
    }
  };

  const handleToggleFormatter = () => {
    let heigh = 0;
    let marginTop = 0;
    let paddingTop = 0;
    let elements = document.getElementsByClassName("chat-formatters-box")[0];
    if (!openFormatter) {
      setTimeout(() => {
        setOpenFormatter(!openFormatter);
      }, 100);
      heigh = "35px";
    } else {
      setOpenFormatter(!openFormatter);
    }
    elements.style.height = heigh;
    elements.style.marginTop = marginTop;
    elements.style.paddingTop = paddingTop;
  };

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: icons.edit,
      handleClick: handleEdit,
    },
    {
      label: "Delete",
      // Same two-tone trash icon as before; shown in red as a destructive action.
      icon: icons.deleteTwoTone,
      danger: true,
      handleClick: handleDelete,
    },
  ];

  useEffect(() => {
    let input_element = document.getElementById("chat-right-bottom");
    if (input_element) {
      setTimeout(() => {
        let input_height = input_element.offsetHeight;
        setInputHeight(input_height);
      }, 200);
    }
  }, [message, openFormatter]);

  const renderLink = (props) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  );

  const canMessage =
    (receiver_profile?.userType !== "member" ||
      friendList.some((item) => item._id === receiver_profile?.id)) &&
    (receiver_profile?.userType !== "coach" ||
      userInfo?.coach?.some((c) => c._id === receiver_profile?.id));

  const messageTime = (message_obj) => (
    <Tooltip
      title={getTimeBasedOnDate(
        message_obj?.createdAt,
        "DD MMM YYYY [at] hh:mm A"
      )}
      arrow
    >
      <span>{getTimeBasedOnDate(message_obj?.createdAt, "hh:mm A")}</span>
    </Tooltip>
  );

  const messageBody = (message_obj) => (
    <>
      {message_obj?.image && (
        <img src={s3baseUrl + message_obj?.image} alt="" loading="lazy"></img>
      )}
      {!!message_obj?.content && (
        <ChatContent content={message_obj.content} renderLink={renderLink} />
      )}
    </>
  );

  const actionButtons = (
    <>
      {(message?.trim() || image || file) && !isLoading && (
        <Tooltip title="Send" placement="top">
          <GrIconButton
            type="submit"
            variant="ghost"
            aria-label="Send message"
            sx={sendSx}
          >
            <GrIcon icon={icons.send} />
          </GrIconButton>
        </Tooltip>
      )}
      {isLoading && (
        <GrIconButton variant="ghost" aria-label="Sending" sx={sendSx}>
          <GrLoader size="sm" inline label="Sending" />
        </GrIconButton>
      )}
      {isUpdate && (
        <Tooltip title="Cancel edit" placement="top">
          <GrIconButton
            variant="ghost"
            aria-label="Cancel edit"
            onClick={handleCancelUpdate}
          >
            <GrIcon icon={icons.close} />
          </GrIconButton>
        </Tooltip>
      )}
    </>
  );

  const previewBox = (src) => (
    <Box sx={previewSx}>
      <img src={src} alt="" />
      <GrIconButton
        variant="ghost"
        size="sm"
        aria-label="Remove image"
        onClick={handleRemoveFile}
        sx={previewRemoveSx}
      >
        <GrIcon icon={icons.close} />
      </GrIconButton>
    </Box>
  );

  return (
    <>
      {!selectedChat ? (
        <Box sx={noChatSx}>
          {/* Empty conversation: big teal chat icon + one short line (messenger standard; approved by Mohsin). */}
          <GrEmptyState
            feature
            icon={<GrIcon icon={icons.chat} />}
            title="Select a chat"
            text="Choose a conversation on the left, or start a new one with +."
          />
        </Box>
      ) : (
        <div
          className="user-chat-box-body"
          style={
            is_popup
              ? {
                  // The popup composer is absolutely positioned at the bottom: leave room for its measured
                  // height (inputHeight, already tracked) so the last message is never hidden under it.
                  paddingBottom: `calc(${inputHeight || 72}px + ${gr.space[3]})`,
                  paddingTop: gr.space[3],
                  overflow: isLoadingMore ? "hidden" : "scroll",
                  backgroundColor: gr.color.surface,
                  paddingLeft: gr.space[3],
                  paddingRight: gr.space[3],
                }
              : {
                  flex: 1,
                  minHeight: 0,
                  height: "auto",
                  overflow: isLoadingMore ? "hidden" : "auto",
                  scrollbarWidth: "thin",
                  padding: `${gr.space[4]} ${gr.space[5]}`,
                }
          }
          id="chat_box_body"
        >
          {isLoadingChat ? (
            <GrLoader sx={{ pt: gr.space[12], pb: 0 }} />
          ) : (
            <>
              {selectedChat && checkLoadMore && (
                <Box sx={{ textAlign: "center", color: gr.color.brand }}>
                  {isLoadingMore ? (
                    <GrLoader size="sm" inline />
                  ) : (
                    <span
                      ref={lastBookElementRef}
                      onClick={loadMoreData}
                      id="load-more-messages"
                    >
                      <CircularProgress
                        onClick={loadMoreData}
                        color="inherit"
                        size={20}
                      />
                    </span>
                  )}
                </Box>
              )}
              {messagesList.map((message_obj, i) => {
                const day = messageDays[i];
                const prevDay = i > 0 ? messageDays[i - 1] : "";
                const wide =
                  message_obj?.message_type == "video" &&
                  !!message_obj?.embed_code;
                // Their avatar only on the last message of a run (same sender, same day); a spacer keeps the others aligned.
                const next = messagesList[i + 1];
                const prev = messagesList[i - 1];
                // Same sender as the message above (and same day): sit close to it.
                const tight =
                  !!prev &&
                  !!prev?.is_self === !!message_obj?.is_self &&
                  messageDays[i - 1] === day;
                const lastOfRun =
                  !next ||
                  next?.is_self ||
                  messageDays[i + 1] !== day;
                return (
                  <React.Fragment
                    key={message_obj?._id ?? message_obj?.messageId ?? i}
                  >
                    {day && day !== prevDay && (
                      <GrChatDateDivider>
                        {day === todayDay ? "Today" : day}
                      </GrChatDateDivider>
                    )}
                    {message_obj?.is_self ? (
                      <GrChatBubble
                        mine
                        tight={tight}
                        wide={wide}
                        time={messageTime(message_obj)}
                        actions={
                          canMessage ? (
                            <CustomPopover
                              menu={MENU_OPTIONS}
                              data={message_obj}
                            />
                          ) : null
                        }
                      >
                        {messageBody(message_obj)}
                      </GrChatBubble>
                    ) : (
                      <GrChatBubble
                        tight={tight}
                        wide={wide}
                        time={messageTime(message_obj)}
                        avatar={
                          is_popup ? null : lastOfRun ? (
                            <GrAvatar
                              src={s3baseUrl + receiver_profile?.profileImage}
                              name={receiver_profile?.name}
                              size={28}
                            />
                          ) : (
                            <Box sx={{ width: 28 }} />
                          )
                        }
                      >
                        {messageBody(message_obj)}
                      </GrChatBubble>
                    )}
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>
      )}
      <Box
        className="chat-right-bottom"
        id="chat-right-bottom"
        sx={receiver_profile || is_popup ? bottomSx : { flex: "none" }}
        style={{
          position: is_popup ? "absolute" : "",
        }}
      >
        {(receiver_profile || is_popup) && (
          <form onSubmit={handleSubmitMessage}>
            {canMessage ? (
              <GrChatComposer
                start={
                  !file &&
                  !image && (
                    <>
                      <input
                        color="primary"
                        accept="image/*"
                        type="file"
                        id="message-chat-upload-button"
                        style={{ display: "none" }}
                        name=""
                        onChange={handleUploadFile}
                      />
                      <Tooltip title="Add Image" placement="top">
                        <GrIconButton
                          variant="ghost"
                          aria-label="Add image"
                          onClick={() => {
                            document.getElementById(
                              "message-chat-upload-button"
                            ) &&
                              document
                                .getElementById("message-chat-upload-button")
                                .click();
                          }}
                        >
                          <GrIcon icon={icons.image} />
                        </GrIconButton>
                      </Tooltip>
                    </>
                  )
                }
                top={
                  <>
                    <Box
                      className="chat-formatters-box"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: gr.space[1],
                        overflow: "hidden",
                        borderBottom: openFormatter
                          ? `1px solid ${gr.color.line2}`
                          : "none",
                      }}
                    >
                      {openFormatter && (
                        <>
                          <GrIconButton
                            variant="ghost"
                            size="sm"
                            aria-label="Bold"
                            onClick={handleOnBold}
                          >
                            <GrIcon icon={icons.formatBold} />
                          </GrIconButton>
                          <GrIconButton
                            variant="ghost"
                            size="sm"
                            aria-label="Italic"
                            onClick={handleOnItalic}
                          >
                            <GrIcon icon={icons.formatItalic} />
                          </GrIconButton>
                          <GrIconButton
                            variant="ghost"
                            size="sm"
                            aria-label="Insert link"
                            onClick={handleOpenLinkPopup}
                          >
                            <GrIcon icon={icons.link} />
                          </GrIconButton>
                        </>
                      )}
                    </Box>
                    {image && previewBox(s3baseUrl + image)}
                    {file && previewBox(get_local_preview(file))}
                  </>
                }
                end={
                  <>
                    <Tooltip
                      title={openFormatter ? "Hide formatting" : "Formatting"}
                      placement="top"
                    >
                      <GrIconButton
                        variant="ghost"
                        aria-label={
                          openFormatter ? "Hide formatting" : "Formatting"
                        }
                        aria-pressed={openFormatter}
                        onClick={handleToggleFormatter}
                      >
                        <GrIcon
                          icon={
                            openFormatter ? icons.chevronDown : icons.textFormat
                          }
                        />
                      </GrIconButton>
                    </Tooltip>
                    {actionButtons}
                  </>
                }
              >
                <TextareaAutosize
                  ref={inputRef}
                  id="inputField"
                  type="text"
                  onKeyDown={handleKeyDown}
                  value={message}
                  maxRows={6}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your message..."
                  aria-label="Message"
                  required={!(image || file)}
                  autoFocus
                />
              </GrChatComposer>
            ) : (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: gr.space[2] }}
              >
                <Box component="p" sx={restrictedSx}>
                  {receiver_profile?.userType !== "member" ||
                  friendList.some((item) => item._id === receiver_profile?.id)
                    ? "This coach isn’t assigned to you anymore, so you can’t send them messages."
                    : "You can't chat with this member because you're not connected as friends."}
                </Box>
                {actionButtons}
              </Box>
            )}
          </form>
        )}
      </Box>
      <CustomConfirmation
        title={"Are you sure you want to delete this message?"}
        open={openDelete}
        setOpen={setOpenDelete}
        handleAgree={handleAgreeDelete}
      />

      <GeneralPopUpModel
        open={openLink}
        setOpen={setOpenLink}
        title={"Insert Link"}
        componentToPassDown={<LinkPopup handleAddLink={handleAddLink} />}
      />
    </>
  );
}

export default ChatMessages;
